import React, { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import oracleDataManager, { createOracleSession } from '../../services/oracleDataManager';
import oracleSessionService from '../../services/oracleSessionService';
import { getDisplayName } from '../../utils/userDisplay';
import VerificationRequired from '../VerificationRequired/VerificationRequired';
import FeedbackButton from '../FeedbackButton/FeedbackButton';
import FeedbackModal from '../FeedbackModal/FeedbackModal';
import './Dashboard.css';

// Force GitHub Actions cache refresh - no testErrorSystem import
const Dashboard = () => {
  const [oracleHistory, setOracleHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [oracleUsage, setOracleUsage] = useState({
    oraclesUsed: 0,
    oracleLimit: 3,
    remainingUses: 3,
    nextReset: null
  });

  // TD3-105: Feedback modal state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  const { user, logout, isVerified } = useAuth();
  const navigate = useNavigate();

  // TD3-200 AGGRESSIVE FIX: Force scroll to top BEFORE anything renders
  useLayoutEffect(() => {
    console.log('🚀 Dashboard useLayoutEffect: Forcing scroll to top');
    // Disable smooth scroll temporarily
    const html = document.documentElement;
    const body = document.body;
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';

    // Force scroll multiple ways
    window.scrollTo(0, 0);
    html.scrollTop = 0;
    body.scrollTop = 0;

    console.log(`📏 Forced scroll - window.scrollY: ${window.scrollY}`);

    // Restore smooth scroll after render
    setTimeout(() => {
      html.style.scrollBehavior = '';
      body.style.scrollBehavior = '';
    }, 100);
  }, []); // Run once on mount

  const loadOracleHistory = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      // TD3-191: Removed email from log to prevent PII exposure
      console.log(`📋 Loading oracle history for user: ${user.id}`);

      // TD3-127: Use oracleSessionService to fetch from PocketBase instead of localStorage
      const result = await oracleSessionService.getUserSessions(user.id);

      if (result.success) {
        // Transform PocketBase records to Dashboard format
        const history = result.sessions.map(record => {
          // PocketBase auto-parses JSON fields, check if already parsed
          const sessionData = typeof record.sessionData === 'string'
            ? JSON.parse(record.sessionData)
            : record.sessionData;

          return {
            // Use PocketBase record.id as unique identifier (prevents duplicate key warnings)
            recordId: record.id,
            sessionId: sessionData.sessionId,
            createdAt: record.completedAt || sessionData.timestamp,
            themeId: sessionData.themeId,
            userQuestion: sessionData.userQuestion,
            isComplete: record.isComplete,
            completedAt: record.completedAt
          };
        });

        console.log(`✅ Loaded ${history.length} oracle sessions from PocketBase`);
        setOracleHistory(history);
      } else {
        console.error('Failed to load oracle sessions:', result.error);
        setError('Erro ao carregar histórico de oráculos');
      }
    } catch (err) {
      setError('Erro ao carregar histórico de oráculos');
      console.error('Error loading oracle history:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadOracleUsage = useCallback(async () => {
    if (!user) return;
    
    try {
      const result = await oracleSessionService.getUserOracleUsage(user.id);
      if (result.success) {
        setOracleUsage({
          oraclesUsed: result.oraclesUsed,
          oracleLimit: result.oracleLimit,
          remainingUses: result.remainingUses,
          nextReset: result.nextReset
        });

        // Debug log for database consistency verification
        if (result.wasResetApplied) {
          // TD3-191: Removed email from log to prevent PII exposure
          console.log(`✅ Period reset applied: PocketBase admin now shows ${result.oraclesUsed}/${result.oracleLimit} for user ${user.id}`);
        }
      }
    } catch (err) {
      console.error('Error loading oracle usage:', err);
      // Don't show error to user - this is not critical
    }
  }, [user]);

  // Redirect if not authenticated or not approved
  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    // TD3-129: GATE 2 - Block access if not approved
    if (user.approvalStatus !== 'approved') {
      // TD3-191: Removed email from log to prevent PII exposure
      console.log(`⚠️ Dashboard access blocked - user not approved: ${user.id}`);
      logout(); // Clear auth and redirect to login
      return;
    }

    loadOracleHistory();
    loadOracleUsage();
  }, [user, navigate, logout, loadOracleHistory, loadOracleUsage]);

  const handleStartNewOracle = async () => {
    try {
      // Create new oracle session
      const newSession = createOracleSession(user.id);
      
      // Save initial session
      await oracleDataManager.saveOracleSession(newSession);
      
      // Navigate to oracle flow
      navigate(`/oracle/${newSession.sessionId}`);
    } catch (err) {
      setError('Erro ao iniciar novo oráculo');
      console.error('Error starting new oracle:', err);
    }
  };

  const handleViewOracle = (recordId) => {
    // TD3-127: Navigate using PocketBase record ID for viewing past oracles
    navigate(`/oracle/${recordId}`);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };


  if (!user) {
    return null; // Will redirect in useEffect
  }

  // TD3-123: Check email verification before allowing access to Oracle
  if (!isVerified) {
    return (
      <div className="dashboard-container">
        {/* Mobile warning */}
        <div className="oracle-mobile-warning">
          <h2>Oráculo dos 12 Raios</h2>
          <p>
            Esta aplicação foi otimizada para laptops e desktops com resolução mínima de 1200px.
            Para uma melhor experiência, acesse em um dispositivo maior.
          </p>
        </div>

        {/* Main dashboard for laptop screens */}
        <div className="dashboard-main">
          {/* TD3-123: Show verification required message with resend option */}
          <VerificationRequired />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Mobile warning */}
      <div className="oracle-mobile-warning">
        <h2>Oráculo dos 12 Raios</h2>
        <p>
          Esta aplicação foi otimizada para laptops e desktops com resolução mínima de 1200px.
          Para uma melhor experiência, acesse em um dispositivo maior.
        </p>
      </div>

      {/* Main dashboard for laptop screens */}
      <div className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-content">
            <div className="logo-section">
              <h1>Oráculo dos 12 Raios</h1>
              <p>Olá, {getDisplayName(user)}</p>
            </div>
            <div className="header-actions">
              <div className="fullscreen-tip">
                <span className="tip-icon">🖥️</span>
                <div className="tip-content">
                  <small>Melhor experiência:</small>
                  <div className="shortcuts">
                    <span><strong>F11</strong> (Windows)</span>
                    <span><strong>⌘⇧F</strong> (Mac)</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => navigate('/settings')}
                className="settings-btn secondary-cta"
              >
                ⚙️ Configurações
              </button>
              <button
                onClick={handleLogout}
                className="logout-btn secondary-cta"
              >
                Sair
              </button>
            </div>
          </div>
        </header>

        <main className="dashboard-content">
          <div className="primary-actions">
            <div className="main-cta-section">
              <h2>Descubra os segredos do universo</h2>
              <p>Consulte o Oráculo dos 12 Raios e receba orientações personalizadas para sua jornada</p>
              
              {/* Oracle Usage Counter */}
              <div className="oracle-usage-counter">
                <div className="usage-info">
                  <span className="usage-text">
                    Consultas restantes este mês: <strong>{oracleUsage.remainingUses}</strong> de {oracleUsage.oracleLimit}
                  </span>
                  <div className="usage-bar">
                    <div
                      className="usage-progress"
                      style={{
                        width: `${(oracleUsage.oraclesUsed / oracleUsage.oracleLimit) * 100}%`
                      }}
                    ></div>
                  </div>
                  {oracleUsage.nextReset && (
                    <div className="reset-info">
                      <span className="reset-text">
                        Próxima renovação: <strong>
                          {oracleUsage.nextReset.toLocaleDateString('pt-BR', {
                            month: 'long',
                            day: 'numeric'
                          })}
                        </strong>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={handleStartNewOracle}
                className="start-oracle-btn primary-cta"
                disabled={oracleUsage.remainingUses <= 0}
              >
                {oracleUsage.remainingUses > 0 ? '✨ Iniciar Novo Oráculo' : '✨ Limite Mensal Atingido'}
              </button>

              {/* TD3-120: Friendlier usage limit message */}
              {oracleUsage.remainingUses <= 0 && (
                <div className="limit-message-container">
                  <p className="limit-message">
                    ✨ Você usou todas as suas consultas deste mês! Suas {oracleUsage.oracleLimit} consultas foram concluídas com sucesso.
                  </p>
                  <p className="limit-message-info">
                    Suas consultas serão renovadas automaticamente em{' '}
                    <strong>
                      {oracleUsage.nextReset?.toLocaleDateString('pt-BR', {
                        day: 'numeric',
                        month: 'long'
                      })}
                    </strong>.
                  </p>
                  <p className="limit-message-tip">
                    💡 Enquanto isso, você pode revisar suas consultas anteriores abaixo ou salvar seus PDFs para referência futura.
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="secondary-actions">
            <div className="history-section">
              <h3>Seus Oráculos Anteriores</h3>
              
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Carregando histórico...</p>
                </div>
              ) : error ? (
                <div className="error-state">
                  <p>{error}</p>
                  <button onClick={loadOracleHistory} className="retry-btn">
                    Tentar novamente
                  </button>
                </div>
              ) : oracleHistory.length === 0 ? (
                <div className="empty-history">
                  <p>Você ainda não consultou o Oráculo dos 12 Raios.</p>
                  <p>Inicie sua primeira consulta clicando no botão acima!</p>
                </div>
              ) : (
                <div className="history-list">
                  {oracleHistory.map((oracle) => (
                    <div key={oracle.recordId} className="history-item">
                      <div className="history-info">
                        <div className="history-date">
                          {formatDate(oracle.createdAt)}
                        </div>
                        <div className="history-question">
                          {oracle.userQuestion ? 
                            `"${oracle.userQuestion.substring(0, 100)}${oracle.userQuestion.length > 100 ? '...' : ''}"` :
                            'Consulta sem pergunta específica'
                          }
                        </div>
                        <div className="history-meta">
                          {/* TD3-120: Theme display hidden per alpha testing feedback */}
                          {/* TD3-202: Only completed oracles shown - no in-progress sessions */}
                          Concluído
                        </div>
                      </div>
                      <div className="history-actions">
                        <button
                          onClick={() => handleViewOracle(oracle.recordId)}
                          className="view-oracle-btn secondary-cta"
                        >
                          Ver Resultado
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>

        <footer className="dashboard-footer">
          <p>
            Oráculo dos 12 Raios • v1.0 • by Buenaar
            {' • '}
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="footer-tos-link"
            >
              Termos de Serviço
            </a>
          </p>
        </footer>

        {/* Error handling system operational - test button removed for production */}
      </div>

      {/* TD3-105: Feedback System */}
      <FeedbackButton onClick={() => setShowFeedbackModal(true)} />
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        currentPage="dashboard"
      />
    </div>
  );
};

export default Dashboard;